import { Calendar, CheckCircle2, Mail } from "lucide-react";
import type { App_Users } from "@/src/gql/graphql";
import type { DetailHeaderModel, HeaderFact } from "../model";
import { formatHeaderDate } from "../model";

/**
 * Employee detail data comes from the inline GET_EMPLOYEE_DETAIL_LITE query
 * (lib/hooks/api/users/employee-graphql-hooks.ts) — an intentional minimal
 * subset, not a codegen Document. The coverage guard checks these against a
 * mirror of that query's selection; only fields the lite query selects may
 * appear here.
 */
export const EMPLOYEE_HEADER_FIELDS = [
  "email",
  "is_active",
  "created_at",
] as const satisfies readonly (keyof App_Users)[];

export type EmployeeHeaderData = Pick<App_Users, (typeof EMPLOYEE_HEADER_FIELDS)[number]>;

/**
 * 1:1 scalar columns edited in the header (dropped from the inline panel).
 * `email` is intentionally NOT here — its config marks it read-only (auth-managed).
 */
export const EMPLOYEE_HEADER_EDITABLE_FIELDS = [
  "is_active",
] as const satisfies readonly (keyof App_Users)[];
type EmployeeHeaderEditableField = (typeof EMPLOYEE_HEADER_EDITABLE_FIELDS)[number];

export function buildEmployeeHeaderModel(employee: EmployeeHeaderData): DetailHeaderModel {
  const facts: HeaderFact[] = [];
  if (employee.email) {
    facts.push({ id: "email", icon: Mail, value: employee.email, kind: "email" });
  }
  facts.push({
    id: "active",
    icon: CheckCircle2,
    value: employee.is_active === false ? "Inactive" : "Active",
    fieldId: "is_active" satisfies EmployeeHeaderEditableField,
  });
  const since = formatHeaderDate(employee.created_at);
  if (since) {
    facts.push({ id: "since", icon: Calendar, label: "Since", value: since });
  }

  return { subtitleSegments: [], facts, metrics: [] };
}
